/** CODE step (PIPE-7): the pipeline's own Python function. Pure helpers shared
 *  by the editor, the step schema and their tests. */

import { THREADS_EXTRA_MB, librariesMemoryMb, usesParallel } from './codeLibraries';

export type CodeMode = 'row' | 'batch';
export type CodeOnError = 'skip' | 'keep' | 'fail';
export interface CodeSchemaColumn { name: string; type: string }

export const CODE_TYPES = ['string', 'integer', 'number', 'boolean', 'date', 'datetime', 'json'] as const;

export const ROW_TEMPLATE = `def transform(row):
    # row es un diccionario con las columnas de la fila.
    # Devuelve un diccionario, una lista de diccionarios o None para descartarla.
    return row
`;

export const BATCH_TEMPLATE = `import pandas as pd

def transform(rows):
    # rows es la lista de filas del lote (hasta batchSize).
    # pandas y numpy están disponibles: devuelve una lista de filas o un DataFrame.
    df = pd.DataFrame(rows)
    return df
`;

export const templateFor = (mode: CodeMode): string => (mode === 'batch' ? BATCH_TEMPLATE : ROW_TEMPLATE);

export const codeMode = (v: unknown): CodeMode => (String(v ?? 'row') === 'batch' ? 'batch' : 'row');

export const codeOnError = (v: unknown): CodeOnError => {
  const s = String(v ?? 'skip');
  return s === 'keep' || s === 'fail' ? s : 'skip';
};

/** The declared output schema, tolerant of a JSON string (the generic panel). */
export const parseCodeSchema = (v: unknown): CodeSchemaColumn[] => {
  let raw: unknown = v;
  if (typeof raw === 'string') {
    try { raw = raw.trim() ? JSON.parse(raw) : []; } catch { return []; }
  }
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: CodeSchemaColumn[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const name = String((item as Record<string, unknown>).name ?? '').trim();
    const type = String((item as Record<string, unknown>).type ?? 'string');
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push({ name, type: (CODE_TYPES as readonly string[]).includes(type) ? type : 'string' });
  }
  return out;
};

/** First 12 hex chars of the code's SHA-256: the same hash the run audit
 *  records, so a run can be matched to the version of the code it ran. */
export const codeHash = async (code: string): Promise<string> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return '';
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(code));
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 12);
};

/** Switching mode keeps the author's code, unless it is still a template. */
export const codeAfterModeSwitch = (code: string, next: CodeMode): string =>
  (!code.trim() || code === ROW_TEMPLATE || code === BATCH_TEMPLATE ? templateFor(next) : code);

// ── PIPE-10: libraries and assistant proposals ──────────────────────────────

/** Batch mode loads pandas and numpy; the sandbox raises a smaller memory
 *  request to this (logic_sandbox/main.py, UDF_LIBRARY_MIN_MEMORY_MB). */
export const LIBRARY_MIN_MEMORY_MB = 192;

/** The memory a partition really takes in the sandbox for this mode, and in
 *  batch mode for the libraries the code imports (PIPE-12). */
export const effectiveMemoryMb = (mode: CodeMode, memoryMb: unknown, code = '', declared: string[] = []): number => {
  const mem = Math.max(64, Math.min(1024, Number(memoryMb) || 512));
  if (mode !== 'batch') return mem;
  // The floor of the libraries, plus the worker threads behind n_jobs (PIPE-15).
  const floor = Math.max(LIBRARY_MIN_MEMORY_MB, librariesMemoryMb(code, declared)) + (usesParallel(code) ? THREADS_EXTRA_MB : 0);
  return Math.max(Math.min(1024, floor), mem);
};

export interface CodeProposal { code: string; by: string; at: string }

/** Code an assistant drafted for the step (config.proposedCode). It never
 *  runs: a person with pipelines:author_custom_code accepts it first. */
export const codeProposal = (config: Record<string, unknown>): CodeProposal | null => {
  const code = typeof config.proposedCode === 'string' ? config.proposedCode : '';
  if (!code.trim()) return null;
  return { code, by: String(config.proposedBy ?? ''), at: String(config.proposedAt ?? '') };
};

/** The config changes that accept a proposal: the code moves to `code` and
 *  the proposal fields go away. Saving then needs the author grant. */
export const acceptProposalChanges = (proposal: CodeProposal): [string, unknown][] => [
  ['code', proposal.code], ['proposedCode', undefined], ['proposedBy', undefined], ['proposedAt', undefined],
];

export const discardProposalChanges = (): [string, unknown][] => [
  ['proposedCode', undefined], ['proposedBy', undefined], ['proposedAt', undefined],
];

/** A CODE node an assistant produced, as a proposal (mirror of
 *  inference_service/code_proposals.py): whatever key carried the code, it
 *  ends in proposedCode and `code` is gone. */
export const asCodeProposal = (config: Record<string, unknown>, by: string): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...config };
  let drafted = '';
  for (const key of ['code', 'source', 'python']) {
    const v = out[key];
    delete out[key];
    if (typeof v === 'string' && v.trim() && !drafted) drafted = v;
  }
  if (!drafted && typeof out.proposedCode === 'string') drafted = out.proposedCode;
  if (!drafted.trim()) { delete out.proposedCode; return out; }
  return { ...out, proposedCode: drafted, proposedBy: String(out.proposedBy || by), proposedAt: String(out.proposedAt || new Date().toISOString()) };
};

// ── CODE-6: incremental runs and partitions ─────────────────────────────────

export type CodeDeleteMode = 'none' | 'soft' | 'hard';
export const DEFAULT_DELETE_COLUMN = '_deleted';

export interface CodeIncrementalConfig {
  enabled: boolean; keyField: string; watermarkField: string;
  /** CODE-7: read only the source's changed records (default on). */
  sourceChanges: boolean;
  /** CODE-7: what a key gone from the source does downstream. CODE-8:
   *  `maxShare` (0-1) holds a connector read that would take away more of the
   *  tracked keys; unset uses the service default. CODE-9: `matchField` is the
   *  destination column that carries the step's key, when the destination
   *  merges by another one (unset: the step's key). */
  deletes: { mode: CodeDeleteMode; column: string; maxShare?: number; matchField?: string };
}

const share = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const f = Number(v);
  return Number.isFinite(f) ? Math.min(1, Math.max(0, f)) : undefined;
};

/** The step's `incremental` block, tolerant of a missing or partial one. */
export const codeIncremental = (v: unknown): CodeIncrementalConfig => {
  const o = v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  const d = o.deletes && typeof o.deletes === 'object' ? (o.deletes as Record<string, unknown>) : {};
  const mode = String(d.mode ?? 'none');
  return {
    enabled: Boolean(o.enabled),
    keyField: String(o.keyField ?? '').trim(),
    watermarkField: String(o.watermarkField ?? '').trim(),
    sourceChanges: o.sourceChanges !== false,
    deletes: {
      mode: mode === 'soft' || mode === 'hard' ? mode : 'none',
      column: String(d.column ?? '').trim() || DEFAULT_DELETE_COLUMN,
      ...(share(d.maxShare) !== undefined ? { maxShare: share(d.maxShare) } : {}),
      ...(String(d.matchField ?? '').trim() ? { matchField: String(d.matchField).trim() } : {}),
    },
  };
};

export const MAX_PARALLEL = 4;
/** What the service answers when the state has not loaded yet (code_step.py). */
export const DEFAULT_TENANT_MAX_PARALLEL = 2;
export const DEFAULT_MEMORY_BUDGET_MB = 384;

/** How many partitions run at once: the request, bounded by the tenant's cap
 *  and by how many children of `memoryMb` fit in the sandbox's memory budget.
 *  Mirrors code_step.effective_parallelism. */
export const parallelPlan = (
  requested: number, memoryMb: number,
  tenantMax = DEFAULT_TENANT_MAX_PARALLEL, budgetMb = DEFAULT_MEMORY_BUDGET_MB,
): { effective: number; boundedBy: 'memory' | 'tenant' | null } => {
  const req = Math.max(1, Math.min(Math.floor(Number(requested) || 1), MAX_PARALLEL));
  const byMemory = Math.max(1, Math.floor(budgetMb / Math.max(64, Number(memoryMb) || 512)));
  const effective = Math.min(req, tenantMax, byMemory);
  if (effective === req) return { effective, boundedBy: null };
  return { effective, boundedBy: byMemory < Math.min(req, tenantMax) ? 'memory' : 'tenant' };
};

/** A watermark value as the editor shows it. */
export const showMark = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(6)));
  return String(v);
};
